import type {
  CartPayload,
  LockedStickerProductArtifact,
  StickerSheetOrder,
  StickerConfiguration,
} from "@/domain/types";
import type { AppLocale } from "@/i18n/routing";
import {
  resolveBackgroundById as defaultResolveBackgroundById,
  resolveIconById as defaultResolveIconById,
  resolveTextColorById as defaultResolveTextColorById,
} from "@/integrations/supabase/design-assets";
import {
  getMixEntries,
  getShopifyStickerSizeCatalog,
  getShopifyStickerSizeCatalogEntry,
  ShopifyProductContractError,
  type ShopifyStickerMixCatalogEntry,
} from "./products";
import {
  getStickerProductKeyFromShopifyLineEntries,
  parseLockedStickerArtifactFromShopifyLineEntries,
  parseStickerSheetOrderFromShopifyLineEntries,
  parseStickerConfigurationFromShopifyLineEntries,
  SHOPIFY_STICKER_PRODUCT_KEY,
} from "./sticker-line-contract";
import {
  buildCustomerFacingStickerAttributes,
  type CustomerFacingBackgroundResolver,
  type CustomerFacingIconResolver,
  type CustomerFacingTextColorResolver,
  type ShopifyCustomerFacingAttribute,
  type ShopifyHiddenStickerLineSnapshot,
} from "./sticker-fulfillment-metafield";

export {
  getShopifyLineEntryValue,
  SHOPIFY_CART_ATTRIBUTE_KEYS,
  SHOPIFY_STICKER_PRODUCT_KEY,
} from "./sticker-line-contract";

export interface ShopifyCartLineAttribute {
  key: string;
  value: string;
}

export interface ShopifyCartLineInput {
  merchandiseId: string;
  quantity: number;
  attributes: ShopifyCartLineAttribute[];
}

export interface ShopifyPreparedCartLine {
  line: ShopifyCartLineInput;
  customerFacingAttributes: ShopifyCustomerFacingAttribute[];
  variantId: string;
}

export async function buildShopifyCartLineAttributes(
  payload: CartPayload,
  options?: {
    locale: AppLocale;
    sizeLabel: string;
    resolveBackgroundById?: CustomerFacingBackgroundResolver;
    resolveIconById?: CustomerFacingIconResolver;
    resolveTextColorById?: CustomerFacingTextColorResolver;
  },
): Promise<ShopifyCartLineAttribute[]> {
  const resolvedOptions = {
    locale: options?.locale ?? "sv",
    sizeLabel: options?.sizeLabel ?? payload.attributes.size ?? "",
  };
  const resolveBackgroundById =
    options?.resolveBackgroundById ??
    ((id: string) => defaultResolveBackgroundById(id, resolvedOptions.locale));
  const resolveIconById =
    options?.resolveIconById ??
    ((id: string) => defaultResolveIconById(id, resolvedOptions.locale));
  const resolveTextColorById =
    options?.resolveTextColorById ??
    ((id: string) => defaultResolveTextColorById(id, resolvedOptions.locale));

  const attributes = await buildCustomerFacingStickerAttributes({
    configuration: payload.configurationSnapshot,
    sheetOrder: payload.sheetOrder,
    locale: resolvedOptions.locale,
    sizeLabel: resolvedOptions.sizeLabel,
    resolveBackgroundById,
    resolveIconById,
    resolveTextColorById,
  });

  return attributes.map((attribute) => ({
    key: attribute.key,
    value: attribute.value,
  }));
}

export function isEditableStickerCartLine(line: {
  attributes: ShopifyCartLineAttribute[];
  quantity: number;
  stickerSnapshot?: ShopifyHiddenStickerLineSnapshot | null;
}): boolean {
  if (line.stickerSnapshot) {
    return true;
  }

  return (
    getStickerProductKeyFromShopifyLineEntries(line.attributes) === SHOPIFY_STICKER_PRODUCT_KEY &&
    getStickerConfigurationFromCartLine(line) !== null &&
    getStickerSheetOrderFromCartLine(line) !== null
  );
}

export function getStickerConfigurationFromCartLine(line: {
  attributes: ShopifyCartLineAttribute[];
  quantity?: number;
  stickerSnapshot?: ShopifyHiddenStickerLineSnapshot | null;
}): StickerConfiguration | null {
  if (line.stickerSnapshot) {
    return {
      ...line.stickerSnapshot.configuration,
      quantity:
        typeof line.quantity === "number" && Number.isInteger(line.quantity)
          ? line.quantity
          : line.stickerSnapshot.configuration.quantity,
    };
  }

  const parseResult = parseStickerConfigurationFromShopifyLineEntries(line.attributes);
  return parseResult.ok ? parseResult.configuration : null;
}

export function getLockedStickerProductArtifactFromCartLine(line: {
  attributes: ShopifyCartLineAttribute[];
  stickerSnapshot?: ShopifyHiddenStickerLineSnapshot | null;
}): LockedStickerProductArtifact | null {
  if (line.stickerSnapshot) {
    return line.stickerSnapshot.lockedArtifact;
  }

  const parseResult = parseLockedStickerArtifactFromShopifyLineEntries(line.attributes);
  return parseResult.ok ? parseResult.lockedArtifact : null;
}

export function getStickerSheetOrderFromCartLine(line: {
  attributes: ShopifyCartLineAttribute[];
  quantity: number;
  stickerSnapshot?: ShopifyHiddenStickerLineSnapshot | null;
}): StickerSheetOrder | null {
  if (line.stickerSnapshot) {
    return {
      ...line.stickerSnapshot.sheetOrder,
      sheetCount: line.quantity,
    };
  }

  const parseResult = parseStickerSheetOrderFromShopifyLineEntries(line.attributes, {
    sheetCount: line.quantity,
  });
  return parseResult.ok ? parseResult.sheetOrder : null;
}

function resolveMixCatalogEntry(
  variantId: string,
  mixEntries: ShopifyStickerMixCatalogEntry[],
): ShopifyStickerMixCatalogEntry {
  const match = mixEntries.find((entry) => entry.variantId === variantId);
  if (!match) {
    throw new ShopifyProductContractError(
      `Shopify mix variant "${variantId}" is not part of the current mix catalog.`,
    );
  }
  return match;
}

export async function prepareShopifyCartLine(
  payload: CartPayload,
  options?: {
    fetch?: typeof fetch;
    locale?: AppLocale;
  },
): Promise<ShopifyPreparedCartLine> {
  const locale = options?.locale ?? "sv";
  const snapshot = payload.configurationSnapshot;

  if (snapshot.kind === "mix") {
    const catalog = await getShopifyStickerSizeCatalog({ fetch: options?.fetch });
    const mixEntry = resolveMixCatalogEntry(snapshot.mixVariantId, getMixEntries(catalog));
    const sizeLabel = locale === "sv" ? mixEntry.displayLabelSv : mixEntry.displayLabelEn;
    const attributes = await buildShopifyCartLineAttributes(payload, {
      locale,
      sizeLabel,
    });

    return {
      line: {
        merchandiseId: mixEntry.variantId,
        quantity: payload.quantity,
        attributes,
      },
      customerFacingAttributes: attributes,
      variantId: mixEntry.variantId,
    };
  }

  if (!payload.attributes.size) {
    throw new Error("prepareShopifyCartLine: payload.attributes.size is required");
  }
  const sizeEntry = await getShopifyStickerSizeCatalogEntry(payload.attributes.size, {
    fetch: options?.fetch,
  });
  const sizeLabel = locale === "sv" ? sizeEntry.displayLabelSv : sizeEntry.displayLabelEn;
  const attributes = await buildShopifyCartLineAttributes(payload, {
    locale,
    sizeLabel,
  });

  return {
    line: {
      merchandiseId: sizeEntry.variantId,
      quantity: payload.quantity,
      attributes,
    },
    customerFacingAttributes: attributes,
    variantId: sizeEntry.variantId,
  };
}

export async function cartPayloadToShopifyCartLine(
  payload: CartPayload,
  options?: {
    fetch?: typeof fetch;
    locale?: AppLocale;
  },
): Promise<ShopifyCartLineInput> {
  const prepared = await prepareShopifyCartLine(payload, options);
  return prepared.line;
}
